<template>
  <div :data-attr="inputIndex">
    <v-checkbox
        v-if="isCheckbox"
        v-model="configOption"
        :ripple="false"
        color="primary"
        @change="emitValue($event)"
        :rules="rulesField"
    >
      <template v-slot:label>
        <span>{{computedName()}}<span style="white-space: nowrap;">&#160;<v-tooltip right max-width="330" v-if="input.description">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" tag="span" fab @click.stop="$event.preventDefault()"><v-icon class="sr-icon-help"/></v-btn>
            </template>
            <span v-html="input.description"></span>
          </v-tooltip>
        </span>
        </span>
      </template>
    </v-checkbox>

    <v-radio-group
        v-else-if="input.type === $options.INPUT_TYPE.RADIOBUTTON"
        v-model="configOption"
        :value="input.default"
        @change="emitValue($event)"
    >
      <v-radio
          v-for="n in input.options"
          :key="n"
          :value="n"
      >
        <template v-slot:label>
          <span>{{ n }}<span style="white-space: nowrap;">&#160;<v-tooltip right max-width="330" v-if="input.description">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                    tag="span"
                    fab
                    @click.stop="$event.preventDefault()"
                >
                  <v-icon class="sr-icon-help"/>
                </v-btn>
              </template>
              <span v-html="input.description"></span>
            </v-tooltip>
          </span>
          </span>
        </template>
      </v-radio>
    </v-radio-group>

    <autocomplete
        v-else-if="input.type === $options.INPUT_TYPE.DROPDOWN"
        v-model="configOption"
        outlined
        :ref="inputIndex"
        :items="objToArr"
        :class="inputIndex"
        :attach="`.${inputIndex}`"
        :label="computedName()"
        @change="emitValue($event)"
        :rules="rulesField"
        :noDataText="$t('noDataAvailable')"
        return-object
    >
      <template slot="append" v-if="input.description">
        <v-tooltip right max-width="330px" :debounce="200">
          <template v-slot:activator="{ on }">
            <v-btn
                icon
                v-on="on"
                slot="activator"
                tag="span"
                fab
                @click.stop="$event.preventDefault()"
                class="input-help"
                :ripple="false"
            >
              <v-icon class="sr-icon-help"/>
            </v-btn>
          </template>
          <span v-html="input.description"></span>
        </v-tooltip>
        <div class="v-input__icon v-input__icon--append">
          <span aria-hidden="true" class="v-icon notranslate theme--light"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="img" aria-hidden="true" class="v-icon__svg"><path d="M7,10L12,15L17,10H7Z"></path></svg></span>
        </div>
      </template>
    </autocomplete>

    <v-text-field
        v-else-if="input.type === $options.INPUT_TYPE.TEXT"
        :label="computedName()"
        outlined
        @change="emitValue($event)"
        :rules="rulesField"
        v-model="configOption"
    >
      <v-tooltip slot="append" right max-width="330px" :debounce="200" v-if="input.description">
        <template v-slot:activator="{ on }">
          <v-btn
              icon
              v-on="on"
              slot="activator"
              tag="span"
              fab
              @click.stop="$event.preventDefault()"
              class="input-help"
              :ripple="false"
          >
            <v-icon class="sr-icon-help"/>
          </v-btn>
        </template>
        <span v-html="input.description"></span>
      </v-tooltip>
    </v-text-field>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { SETTINGS_GETTERS, SETTING_ACTIONS } from "@/store/modules/settings";
  import { INPUT_TYPE, LANG } from '@/constants';
  import { Autocomplete } from '@/shared/components/Autocomplete';

  export default {
    name: "CustomField",
    INPUT_TYPE,
    components: {
      Autocomplete,
    },
    props: {
      input: { type: [Object, Array], default: () => ({}) },
      modelValue: [String, Boolean, Number, Object]
    },
    data: () => ({
      checkbox: false,
      configOption: {},
      objToArr: {},
    }),
    async mounted() {
      if (!this.lang) {
        this.lang =  await this.setLang();
      };
    },
    created() {
      this.configOption = this.isCheckbox ? this.input.default === 'on' : this.input.default || this.input.options[0];
      if(this.input.type === INPUT_TYPE.DROPDOWN) {

        if(this.isCms) {
          this.objToArr = [
            { text: this.$t('yes'), value: this.input.options[0] },
            { text: this.$t('no'), value: this.input.options[1] }
          ];
        } else {
          this.objToArr = Object.entries(this.input.options).map(([value, text]) => ({value, text}));
        }

        this.configOption = this.input.default ? (this.objToArr.find(obj => obj.value === this.input.default) ?? this.input.default) : this.objToArr[0];
      }
      this.emitValue(this.configOption);
    },
    computed: {
      ...mapGetters('settings', { lang: SETTINGS_GETTERS.LANG }),
      isCheckbox() {
        return this.input.type === INPUT_TYPE.CHECKBOX || this.input.type === INPUT_TYPE.TICKBOX;
      },
      rulesField() {
        const rules = this.$attrs.rules || [];
        if (this.input.required) {
          rules.push(v => (this.input.type === INPUT_TYPE.TEXT ? !!v?.trim() : !!v) || this.$t('validation.required'));
        }

        return rules;
      },
      inputIndex() {
        return `custom-field-${this.input.domainIndex}${this.input.index}`
      },
      isCms() {
        return this.input.name === "Установить CMS автоматически";
      },
      externalValue() {
        return this.$attrs.value;
      }
    },
    methods: {
      computedName() {
        const langKey = LANG[this.lang];
        const siteLangKey = LANG[`SITE_LANG_${langKey}`];

        let { name } = this.input;

        if (this.input.translated_names && this.input.translated_names[siteLangKey]) {
          name = this.input.translated_names[siteLangKey];
        } else if (this.input.translated_names && this.input.translated_names[langKey.toLowerCase()]) {
          name = this.input.translated_names[langKey.toLowerCase()];
        }

        return name;
      },
      emitValue(input) {
        let val = input;
        if (this.isCheckbox) {
          val = this.configOption;
        } else if (this.input.type === INPUT_TYPE.DROPDOWN) {
          val = this.input.addOpt && !this.isCms ? input?.text : input?.value || this.configOption;
        }
        this.$emit('input', val);
      },
    },
    watch: {
      lang() {
        this.computedName();
      },
      externalValue: {
        handler(value) {
          this.configOption = value;
          if(this.input.type === INPUT_TYPE.TEXT && value) {
            this.emitValue(value)
          }
          this.emitValue(value)
        },
        immediate: true,
        deep: true
      }
    }
  }
</script>
